import {
  FC,
  type ChangeEvent,
  type MouseEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import cx from "classnames"
import { BiPlus as AddIcon } from "react-icons/bi"
import Button from "@/components/Button"
import ConfirmModal from "@/components/ConfirmModal"
import ErrorMessage from "@/components/ErrorMessage"
import Loader from "@/components/Loader"
import OutlineButton from "@/components/OutlineButton"
import Radio from "@/components/Radio"
import RadioGroup from "@/components/RadioGroup"
import Select from "@/components/Select"
import Textarea from "@/components/Textarea"
import TextField from "@/components/TextField"
import { getErrorMessage } from "@/core/api/helpers"
import { useAppDispatch } from "@/redux/hooks"
import {
  useGetSpamTestSettingsQuery,
  useResetSpamTestSettingsRoundMutation,
  useUpdateSpamTestSettingsMutation,
} from "@/redux/services/spamTestSettingsApi"
import { setToast } from "@/redux/toastSlice"
import { downloadCsvString, readFileAsText } from "@/utils/csv"
import { csvToRounds, roundsToCsv } from "./csv"
import type {
  AiSettings,
  CopyFieldConfig,
  CopyFieldMode,
  ReasoningEffort,
  Round,
  SignatureFieldConfig,
  SignatureFieldMode,
  SpamTestSettings,
  SpamTestSettingsPayload,
  Variation,
  VariationFields,
} from "@/types/entities/spamTestSettings"

const COMMON_AI_MODELS = [
  "gpt-5.5",
  "gpt-5.4-mini",
  "gpt-5.4-nano",
  "gpt-4.1",
  "gpt-4o",
  "gpt-4o-mini",
] as const

const CUSTOM_MODEL_VALUE = "__custom__"

const AI_MODEL_OPTIONS = [
  ...COMMON_AI_MODELS.map((model) => ({ label: model, value: model })),
  { label: "Custom...", value: CUSTOM_MODEL_VALUE },
]

const REASONING_EFFORT_OPTIONS = [
  { label: "Default", value: "" },
  { label: "None", value: "none" },
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
]

const COPY_FIELD_MODE_VALUES: CopyFieldMode[] = ["original", "prompt", "blank"]
const SIGNATURE_FIELD_MODE_VALUES: SignatureFieldMode[] = ["original", "blank"]

type Props = {
  className?: string
}

type CopyFieldKey = "subject" | "greeting" | "body" | "signoff" | "unsub"
type SignatureFieldKey = "title" | "company" | "website" | "phoneNumber"
type TuningField = "seedsPerEliminationVariant" | "variantsPerSampleBatch"
type TuningForm = Pick<SpamTestSettings, TuningField>
type SavingSection =
  | "tuning"
  | "ai"
  | "import"
  | `round-${number}`
  | `reset-${number}`
  | null

type RoundValidationErrors = {
  round?: string
  variationNames: Record<number, string>
  prompts: Record<string, string>
}

const DEFAULT_TUNING_FORM: TuningForm = {
  seedsPerEliminationVariant: 6,
  variantsPerSampleBatch: 4,
}

const DEFAULT_AI_SETTINGS: AiSettings = {
  model: "",
  reasoningEffort: "",
  temperature: null,
  topP: null,
  timeoutMs: 30000,
  maxRetries: 2,
}

const TUNING_FIELDS: {
  key: TuningField
  label: string
  min: number
  helpText: string
}[] = [
  {
    key: "seedsPerEliminationVariant",
    label: "Seeds per variation",
    min: 1,
    helpText:
      "Seed inboxes each variation is tested against. A variation must hit a perfect inbox score on every seed to win.",
  },
  {
    key: "variantsPerSampleBatch",
    label: "Variations per batch",
    min: 1,
    helpText:
      "How many untried variations are drawn at random per batch. If none pass, another batch is drawn until one wins or the pool is exhausted.",
  },
]

const COPY_FIELDS: {
  key: CopyFieldKey
  label: string
  promptPlaceholder: string
}[] = [
  {
    key: "subject",
    label: "Subject",
    promptPlaceholder: "Describe how AI should rewrite the subject.",
  },
  {
    key: "greeting",
    label: "Greeting",
    promptPlaceholder: "Describe how AI should rewrite the greeting.",
  },
  {
    key: "body",
    label: "Body",
    promptPlaceholder: "Describe how AI should rewrite the body.",
  },
  {
    key: "signoff",
    label: "Signoff",
    promptPlaceholder: "Describe how AI should rewrite the signoff.",
  },
  {
    key: "unsub",
    label: "Unsub",
    promptPlaceholder: "Describe how AI should rewrite the unsubscribe line.",
  },
]

const SIGNATURE_FIELDS: {
  key: SignatureFieldKey
  label: string
}[] = [
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "website", label: "Website" },
  { key: "phoneNumber", label: "Phone" },
]

const createCopyField = (
  mode: CopyFieldMode = "original",
  prompt = "",
): CopyFieldConfig => ({ mode, prompt })

const createSignatureField = (
  mode: SignatureFieldMode = "original",
): SignatureFieldConfig => ({ mode })

const createFields = (
  copyOverrides: Partial<Record<CopyFieldKey, CopyFieldConfig>> = {},
  signatureOverrides: Partial<
    Record<SignatureFieldKey, SignatureFieldConfig>
  > = {},
): VariationFields => ({
  subject: copyOverrides.subject ?? createCopyField(),
  greeting: copyOverrides.greeting ?? createCopyField(),
  body: copyOverrides.body ?? createCopyField(),
  signoff: copyOverrides.signoff ?? createCopyField(),
  unsub: copyOverrides.unsub ?? createCopyField(),
  title: signatureOverrides.title ?? createSignatureField(),
  company: signatureOverrides.company ?? createSignatureField(),
  website: signatureOverrides.website ?? createSignatureField(),
  phoneNumber: signatureOverrides.phoneNumber ?? createSignatureField(),
})

const createVariation = (
  variationName?: string,
  copyOverrides: Partial<Record<CopyFieldKey, CopyFieldConfig>> = {},
  signatureOverrides: Partial<
    Record<SignatureFieldKey, SignatureFieldConfig>
  > = {},
): Variation => ({
  ...(variationName ? { variationName } : {}),
  fields: createFields(copyOverrides, signatureOverrides),
})

const isCopyFieldMode = (value: unknown): value is CopyFieldMode =>
  COPY_FIELD_MODE_VALUES.includes(value as CopyFieldMode)

const isSignatureFieldMode = (value: unknown): value is SignatureFieldMode =>
  SIGNATURE_FIELD_MODE_VALUES.includes(value as SignatureFieldMode)

const normalizeCopyField = (
  field?: Partial<CopyFieldConfig>,
): CopyFieldConfig => ({
  mode: isCopyFieldMode(field?.mode) ? field.mode : "original",
  prompt: typeof field?.prompt === "string" ? field.prompt : "",
})

const normalizeSignatureField = (
  field?: Partial<SignatureFieldConfig>,
): SignatureFieldConfig => ({
  mode: isSignatureFieldMode(field?.mode) ? field.mode : "original",
})

const normalizeVariation = (variation?: Partial<Variation>): Variation => ({
  ...(variation?.variationName
    ? { variationName: variation.variationName }
    : {}),
  fields: {
    subject: normalizeCopyField(variation?.fields?.subject),
    greeting: normalizeCopyField(variation?.fields?.greeting),
    body: normalizeCopyField(variation?.fields?.body),
    signoff: normalizeCopyField(variation?.fields?.signoff),
    unsub: normalizeCopyField(variation?.fields?.unsub),
    title: normalizeSignatureField(variation?.fields?.title),
    company: normalizeSignatureField(variation?.fields?.company),
    website: normalizeSignatureField(variation?.fields?.website),
    phoneNumber: normalizeSignatureField(variation?.fields?.phoneNumber),
  },
})

const normalizeRound = (round: Partial<Round>, index: number): Round => {
  const roundNumber = Number.isInteger(round.roundNumber)
    ? Number(round.roundNumber)
    : index + 1
  const variations = Array.isArray(round.variations)
    ? round.variations.map(normalizeVariation)
    : []

  return {
    roundNumber,
    variations: variations.length ? variations : [createVariation()],
  }
}

const settingsToRounds = (settings?: SpamTestSettings): Round[] => {
  if (Array.isArray(settings?.rounds) && settings.rounds.length) {
    return settings.rounds.map(normalizeRound)
  }

  return []
}

const settingsToTuningForm = (settings?: SpamTestSettings): TuningForm => ({
  seedsPerEliminationVariant:
    settings?.seedsPerEliminationVariant ??
    DEFAULT_TUNING_FORM.seedsPerEliminationVariant,
  variantsPerSampleBatch:
    settings?.variantsPerSampleBatch ??
    DEFAULT_TUNING_FORM.variantsPerSampleBatch,
})

const settingsToAiForm = (settings?: SpamTestSettings): AiSettings => ({
  model: settings?.ai?.model ?? DEFAULT_AI_SETTINGS.model,
  reasoningEffort:
    settings?.ai?.reasoningEffort ?? DEFAULT_AI_SETTINGS.reasoningEffort,
  temperature: settings?.ai?.temperature ?? DEFAULT_AI_SETTINGS.temperature,
  topP: settings?.ai?.topP ?? DEFAULT_AI_SETTINGS.topP,
  timeoutMs: settings?.ai?.timeoutMs ?? DEFAULT_AI_SETTINGS.timeoutMs,
  maxRetries: settings?.ai?.maxRetries ?? DEFAULT_AI_SETTINGS.maxRetries,
})

const getAiModelSelection = (model: string) => {
  if (!model) return undefined

  return COMMON_AI_MODELS.includes(model as (typeof COMMON_AI_MODELS)[number])
    ? model
    : CUSTOM_MODEL_VALUE
}

const sanitizeCopyField = (field: CopyFieldConfig): CopyFieldConfig => {
  return {
    mode: field.mode,
    prompt: field.mode === "prompt" ? field.prompt.trim() : "",
  }
}

const sanitizeSignatureField = (
  field: SignatureFieldConfig,
): SignatureFieldConfig => ({ mode: field.mode })

const sanitizeVariation = (variation: Variation): Variation => {
  const variationName = variation.variationName?.trim()

  return {
    ...(variationName ? { variationName } : {}),
    fields: {
      subject: sanitizeCopyField(variation.fields.subject),
      greeting: sanitizeCopyField(variation.fields.greeting),
      body: sanitizeCopyField(variation.fields.body),
      signoff: sanitizeCopyField(variation.fields.signoff),
      unsub: sanitizeCopyField(variation.fields.unsub),
      title: sanitizeSignatureField(variation.fields.title),
      company: sanitizeSignatureField(variation.fields.company),
      website: sanitizeSignatureField(variation.fields.website),
      phoneNumber: sanitizeSignatureField(variation.fields.phoneNumber),
    },
  }
}

const sanitizeRound = (round: Round): Round => ({
  roundNumber: round.roundNumber,
  variations: round.variations.map(sanitizeVariation),
})

const promptErrorKey = (variationIndex: number, field: CopyFieldKey) =>
  `${variationIndex}.${field}`

const validateRound = (round: Round): RoundValidationErrors => {
  const errors: RoundValidationErrors = {
    variationNames: {},
    prompts: {},
  }

  if (!round.variations.length) {
    errors.round = "At least 1 variation is required."
  }

  const variationNameIndexes = new Map<string, number[]>()
  round.variations.forEach((variation, variationIndex) => {
    const variationName = variation.variationName?.trim()
    if (!variationName) return

    variationNameIndexes.set(variationName, [
      ...(variationNameIndexes.get(variationName) ?? []),
      variationIndex,
    ])
  })

  variationNameIndexes.forEach((indexes) => {
    if (indexes.length < 2) return

    indexes.forEach((variationIndex) => {
      errors.variationNames[variationIndex] =
        "Variation names must be unique within this round."
    })
  })

  round.variations.forEach((variation, variationIndex) => {
    COPY_FIELDS.forEach(({ key, label }) => {
      const field = variation.fields[key]
      if (field.mode === "prompt" && !field.prompt?.trim()) {
        errors.prompts[
          promptErrorKey(variationIndex, key)
        ] = `${label} prompt is required.`
      }
    })
  })

  return errors
}

const roundHasErrors = ({
  round,
  variationNames,
  prompts,
}: RoundValidationErrors) =>
  Boolean(
    round || Object.keys(variationNames).length || Object.keys(prompts).length,
  )

const allSignatureFieldsBlank = (fields: VariationFields) =>
  SIGNATURE_FIELDS.every(({ key }) => fields[key].mode === "blank")

const SettingsSection: FC<{
  title: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
}> = ({ title, actions, children, className }) => {
  const preventSummaryToggle = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  return (
    <details
      open
      className={cx(
        "rounded-md border border-outline bg-[#F6F6F9] overflow-hidden",
        className,
      )}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 bg-white p-4 text-sm font-semibold marker:hidden [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        {actions && (
          <div
            className="flex flex-wrap items-center justify-end gap-3"
            onClick={preventSummaryToggle}
          >
            {actions}
          </div>
        )}
      </summary>
      <div className="border-t border-outline space-y-4 bg-[#F8F8F8] px-5 py-4">
        {children}
      </div>
    </details>
  )
}

const CopyFieldRow: FC<{
  roundNumber: number
  variationIndex: number
  fieldKey: CopyFieldKey
  label: string
  value: CopyFieldConfig
  promptPlaceholder: string
  promptError?: string
  disabled?: boolean
  onModeChange: (mode: CopyFieldMode) => void
  onPromptChange: (prompt: string) => void
}> = ({
  roundNumber,
  variationIndex,
  fieldKey,
  label,
  value,
  promptPlaceholder,
  promptError,
  disabled,
  onModeChange,
  onPromptChange,
}) => {
  const testIdPrefix = `round-${roundNumber}-variation-${
    variationIndex + 1
  }-${fieldKey}`

  return (
    <div
      className="rounded-md border border-outline bg-white p-3"
      data-testid={`${testIdPrefix}-field`}
    >
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[160px_minmax(0,1fr)] lg:items-center">
        <span className="text-sm font-medium">{label}</span>
        <RadioGroup
          value={value.mode}
          onChange={({ target: { value: mode } }) =>
            onModeChange(mode as CopyFieldMode)
          }
          className="flex flex-wrap gap-x-5 gap-y-2"
          disabled={disabled}
        >
          <Radio
            value="original"
            data-testid={`${testIdPrefix}-mode-original`}
            disabled={disabled}
          >
            Original
          </Radio>
          <Radio
            value="prompt"
            data-testid={`${testIdPrefix}-mode-prompt`}
            disabled={disabled}
          >
            Prompt
          </Radio>
          <Radio
            value="blank"
            data-testid={`${testIdPrefix}-mode-blank`}
            disabled={disabled}
          >
            Blank
          </Radio>
        </RadioGroup>
      </div>
      {value.mode === "prompt" && (
        <Textarea
          topLeftLabel={`${label} prompt`}
          value={value.prompt ?? ""}
          onChange={({ target: { value: prompt } }) => onPromptChange(prompt)}
          placeholder={promptPlaceholder}
          className="mt-3 min-h-[104px]"
          bottomLeftLabel={
            promptError ? (
              <span className="text-error">{promptError}</span>
            ) : null
          }
          disabled={disabled}
          data-testid={`${testIdPrefix}-prompt`}
        />
      )}
    </div>
  )
}

const SignatureFieldRow: FC<{
  roundNumber: number
  variationIndex: number
  fieldKey: SignatureFieldKey
  label: string
  value: SignatureFieldConfig
  disabled?: boolean
  onModeChange: (mode: SignatureFieldMode) => void
}> = ({
  roundNumber,
  variationIndex,
  fieldKey,
  label,
  value,
  disabled,
  onModeChange,
}) => {
  const testIdPrefix = `round-${roundNumber}-variation-${
    variationIndex + 1
  }-${fieldKey}`

  return (
    <div
      className="grid grid-cols-1 gap-3 rounded-md border border-outline bg-white p-3 lg:grid-cols-[160px_minmax(0,1fr)] lg:items-center"
      data-testid={`${testIdPrefix}-field`}
    >
      <span className="text-sm font-medium">{label}</span>
      <RadioGroup
        value={value.mode}
        onChange={({ target: { value: mode } }) =>
          onModeChange(mode as SignatureFieldMode)
        }
        className="flex flex-wrap gap-x-5 gap-y-2"
        disabled={disabled}
      >
        <Radio
          value="original"
          data-testid={`${testIdPrefix}-mode-original`}
          disabled={disabled}
        >
          Original
        </Radio>
        <Radio
          value="blank"
          data-testid={`${testIdPrefix}-mode-blank`}
          disabled={disabled}
        >
          Blank
        </Radio>
      </RadioGroup>
    </div>
  )
}

const VariationCard: FC<{
  roundNumber: number
  variation: Variation
  variationIndex: number
  variationCount: number
  errors?: RoundValidationErrors
  disabled?: boolean
  onVariationNameChange: (value: string) => void
  onCopyModeChange: (field: CopyFieldKey, mode: CopyFieldMode) => void
  onCopyPromptChange: (field: CopyFieldKey, prompt: string) => void
  onSignatureModeChange: (
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => void
  onRemoveVariation: () => void
}> = ({
  roundNumber,
  variation,
  variationIndex,
  variationCount,
  errors,
  disabled,
  onVariationNameChange,
  onCopyModeChange,
  onCopyPromptChange,
  onSignatureModeChange,
  onRemoveVariation,
}) => {
  const fallbackName = `R${roundNumber}V${variationIndex + 1}`
  const displayName = variation.variationName?.trim() || fallbackName
  const testIdPrefix = `round-${roundNumber}-variation-${variationIndex + 1}`

  return (
    <div
      className="rounded-lg border border-outline bg-white p-4 shadow-sm"
      data-testid={`round-${roundNumber}-variation-card`}
    >
      <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
        <h3 className="text-base font-semibold">
          Variation {variationIndex + 1}: {displayName}
        </h3>
        <OutlineButton
          type="button"
          className="btn-sm"
          onClick={onRemoveVariation}
          disabled={disabled || variationCount <= 1}
          data-testid={`${testIdPrefix}-remove`}
        >
          Remove variation
        </OutlineButton>
      </div>

      <TextField
        topLeftLabel="Variation name (optional)"
        value={variation.variationName ?? ""}
        placeholder={fallbackName}
        onChange={({ target: { value } }) => onVariationNameChange(value)}
        className="input-sm"
        rootClassName="mt-4"
        bottomLeftLabel={
          errors?.variationNames[variationIndex] ? (
            <span className="text-error">
              {errors.variationNames[variationIndex]}
            </span>
          ) : null
        }
        disabled={disabled}
        data-testid={`${testIdPrefix}-name`}
      />

      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-semibold">Copy fields</h4>
        {COPY_FIELDS.map(({ key, label, promptPlaceholder }) => (
          <CopyFieldRow
            key={key}
            roundNumber={roundNumber}
            variationIndex={variationIndex}
            fieldKey={key}
            label={label}
            value={variation.fields[key]}
            promptPlaceholder={promptPlaceholder}
            promptError={errors?.prompts[promptErrorKey(variationIndex, key)]}
            disabled={disabled}
            onModeChange={(mode) => onCopyModeChange(key, mode)}
            onPromptChange={(prompt) => onCopyPromptChange(key, prompt)}
          />
        ))}
      </div>

      <div className="mt-4 space-y-3">
        <h4 className="text-sm font-semibold">Signature sub-fields</h4>
        {SIGNATURE_FIELDS.map(({ key, label }) => (
          <SignatureFieldRow
            key={key}
            roundNumber={roundNumber}
            variationIndex={variationIndex}
            fieldKey={key}
            label={label}
            value={variation.fields[key]}
            disabled={disabled}
            onModeChange={(mode) => onSignatureModeChange(key, mode)}
          />
        ))}
      </div>

      {(variation.fields.unsub.mode === "blank" ||
        allSignatureFieldsBlank(variation.fields)) && (
        <div className="mt-4 space-y-2">
          {variation.fields.unsub.mode === "blank" && (
            <p className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
              Blank unsubscribe may affect compliance.
            </p>
          )}
          {allSignatureFieldsBlank(variation.fields) && (
            <p className="rounded-md border border-info/40 bg-info/10 px-3 py-2 text-sm text-info">
              Full signature removed.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

const RoundSection: FC<{
  round: Round
  errors?: RoundValidationErrors
  disabled?: boolean
  savingSection: SavingSection
  onAddVariation: () => void
  onResetRound: () => void
  onSaveRound: () => void
  onRemoveVariation: (variationIndex: number) => void
  onVariationNameChange: (variationIndex: number, value: string) => void
  onCopyModeChange: (
    variationIndex: number,
    field: CopyFieldKey,
    mode: CopyFieldMode,
  ) => void
  onCopyPromptChange: (
    variationIndex: number,
    field: CopyFieldKey,
    prompt: string,
  ) => void
  onSignatureModeChange: (
    variationIndex: number,
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => void
}> = ({
  round,
  errors,
  disabled,
  savingSection,
  onAddVariation,
  onResetRound,
  onSaveRound,
  onRemoveVariation,
  onVariationNameChange,
  onCopyModeChange,
  onCopyPromptChange,
  onSignatureModeChange,
}) => {
  const roundNumber = round.roundNumber

  return (
    <SettingsSection
      title={`Elimination Round ${roundNumber}`}
      actions={
        <>
          <OutlineButton
            type="button"
            className="btn-sm"
            onClick={onAddVariation}
            disabled={disabled}
            data-testid={`round-${roundNumber}-add-variation`}
          >
            <AddIcon className="h-5 w-5" />
            <span>Add Variation</span>
          </OutlineButton>
          <OutlineButton
            type="button"
            className="btn-sm"
            onClick={onResetRound}
            disabled={disabled}
            loading={savingSection === `reset-${roundNumber}`}
            data-testid={`round-${roundNumber}-reset`}
          >
            Reset to defaults
          </OutlineButton>
        </>
      }
    >
      {errors?.round && (
        <p className="rounded-md border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
          {errors.round}
        </p>
      )}
      <div className="space-y-4">
        {round.variations.map((variation, variationIndex) => (
          <VariationCard
            key={`${roundNumber}-${variationIndex}`}
            roundNumber={roundNumber}
            variation={variation}
            variationIndex={variationIndex}
            variationCount={round.variations.length}
            errors={errors}
            disabled={disabled}
            onVariationNameChange={(value) =>
              onVariationNameChange(variationIndex, value)
            }
            onCopyModeChange={(field, mode) =>
              onCopyModeChange(variationIndex, field, mode)
            }
            onCopyPromptChange={(field, prompt) =>
              onCopyPromptChange(variationIndex, field, prompt)
            }
            onSignatureModeChange={(field, mode) =>
              onSignatureModeChange(variationIndex, field, mode)
            }
            onRemoveVariation={() => onRemoveVariation(variationIndex)}
          />
        ))}
      </div>
      <Button
        type="button"
        onClick={onSaveRound}
        disabled={disabled}
        loading={savingSection === `round-${roundNumber}`}
        className="btn-sm min-w-[112px]"
        data-testid={`round-${roundNumber}-save`}
      >
        Save Round {roundNumber}
      </Button>
    </SettingsSection>
  )
}

const AdminSpamTestSettingsPage: FC<Props> = ({
  className,
  ...rest
}: Props) => {
  const dispatch = useAppDispatch()
  const { data: settings, isLoading, isError } = useGetSpamTestSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateSpamTestSettingsMutation()
  const [resetRoundToDefaults, { isLoading: isResettingRound }] =
    useResetSpamTestSettingsRoundMutation()
  const [savingSection, setSavingSection] = useState<SavingSection>(null)
  const [tuningForm, setTuningForm] = useState<TuningForm>(DEFAULT_TUNING_FORM)
  const [aiForm, setAiForm] = useState<AiSettings>(DEFAULT_AI_SETTINGS)
  const [aiModelSelection, setAiModelSelection] = useState<string | undefined>()
  const [roundsForm, setRoundsForm] = useState<Round[]>([])
  const [roundSaveAttempted, setRoundSaveAttempted] = useState<
    Record<number, boolean>
  >({})
  const [resetRoundNumber, setResetRoundNumber] = useState<number | null>(null)
  const importInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!settings) return

    const nextAiForm = settingsToAiForm(settings)
    setTuningForm(settingsToTuningForm(settings))
    setAiForm(nextAiForm)
    setAiModelSelection(getAiModelSelection(nextAiForm.model))
    setRoundsForm(settingsToRounds(settings))
    setRoundSaveAttempted({})
  }, [settings])

  const tuningFormValid = useMemo(
    () =>
      TUNING_FIELDS.every(({ key, min }) => {
        const value = tuningForm[key]
        return Number.isInteger(value) && value >= min
      }),
    [tuningForm],
  )

  const aiFormValid = useMemo(() => {
    const samplingValid = (value: number | null, max: number) =>
      value === null || (Number.isFinite(value) && value >= 0 && value <= max)

    return (
      Number.isInteger(aiForm.timeoutMs) &&
      aiForm.timeoutMs >= 1 &&
      Number.isInteger(aiForm.maxRetries) &&
      aiForm.maxRetries >= 0 &&
      samplingValid(aiForm.temperature, 2) &&
      samplingValid(aiForm.topP, 1)
    )
  }, [aiForm.maxRetries, aiForm.timeoutMs, aiForm.temperature, aiForm.topP])

  const roundValidationByNumber = useMemo(
    () =>
      roundsForm.reduce<Record<number, RoundValidationErrors>>(
        (validationByRound, round) => ({
          ...validationByRound,
          [round.roundNumber]: validateRound(round),
        }),
        {},
      ),
    [roundsForm],
  )

  const formDisabled = isSaving || isResettingRound

  const handleTuningChange =
    (field: TuningField) =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setTuningForm((current) => ({
        ...current,
        [field]: value === "" ? 0 : Number(value),
      }))
    }

  const handleAiNumberChange =
    (field: "timeoutMs" | "maxRetries") =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setAiForm((current) => ({
        ...current,
        [field]: value === "" ? 0 : Number(value),
      }))
    }

  const handleAiSamplingChange =
    (field: "temperature" | "topP") =>
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      setAiForm((current) => ({
        ...current,
        [field]: value === "" ? null : Number(value),
      }))
    }

  const handleReasoningEffortChange = (value?: string) => {
    setAiForm((current) => ({
      ...current,
      reasoningEffort: (value ?? "") as ReasoningEffort,
    }))
  }

  const handleAiModelChange = (value?: string) => {
    setAiModelSelection(value)
    if (!value) {
      setAiForm((current) => ({ ...current, model: "" }))
      return
    }

    if (value !== CUSTOM_MODEL_VALUE) {
      setAiForm((current) => ({ ...current, model: value }))
    } else if (
      COMMON_AI_MODELS.includes(
        aiForm.model as (typeof COMMON_AI_MODELS)[number],
      )
    ) {
      setAiForm((current) => ({ ...current, model: "" }))
    }
  }

  const updateRound = (
    roundNumber: number,
    updater: (round: Round) => Round,
  ) => {
    setRoundsForm((current) =>
      current.map((round) =>
        round.roundNumber === roundNumber ? updater(round) : round,
      ),
    )
  }

  const updateVariation = (
    roundNumber: number,
    variationIndex: number,
    updater: (variation: Variation) => Variation,
  ) => {
    updateRound(roundNumber, (round) => ({
      ...round,
      variations: round.variations.map((variation, index) =>
        index === variationIndex ? updater(variation) : variation,
      ),
    }))
  }

  const saveSettings = async (
    section: Exclude<SavingSection, null>,
    payload: SpamTestSettingsPayload,
  ) => {
    setSavingSection(section)
    try {
      await updateSettings(payload).unwrap()
      dispatch(
        setToast({
          show: true,
          variant: "success",
          message: "Spam test settings saved",
        }),
      )
      return true
    } catch (error) {
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: getErrorMessage(error, true),
        }),
      )
      return false
    } finally {
      setSavingSection(null)
    }
  }

  const handleSaveTuning = () => {
    if (!tuningFormValid) return

    saveSettings("tuning", tuningForm)
  }

  const handleSaveAi = () => {
    if (!aiFormValid) return

    saveSettings("ai", {
      ai: {
        model: aiForm.model.trim(),
        reasoningEffort: aiForm.reasoningEffort,
        temperature: aiForm.temperature,
        topP: aiForm.topP,
        timeoutMs: aiForm.timeoutMs,
        maxRetries: aiForm.maxRetries,
      },
    })
  }

  const handleAddVariation = (roundNumber: number) => {
    updateRound(roundNumber, (round) => ({
      ...round,
      variations: [...round.variations, createVariation()],
    }))
  }

  const handleRemoveVariation = (
    roundNumber: number,
    variationIndex: number,
  ) => {
    const round = roundsForm.find((round) => round.roundNumber === roundNumber)
    if (!round || round.variations.length <= 1) return

    const variation = round.variations[variationIndex]
    const variationName =
      variation.variationName?.trim() || `R${roundNumber}V${variationIndex + 1}`

    if (!window.confirm(`Remove variation ${variationName}?`)) return

    updateRound(roundNumber, (currentRound) => ({
      ...currentRound,
      variations: currentRound.variations.filter(
        (_, index) => index !== variationIndex,
      ),
    }))
  }

  const handleVariationNameChange = (
    roundNumber: number,
    variationIndex: number,
    value: string,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      variationName: value,
    }))
  }

  const handleCopyModeChange = (
    roundNumber: number,
    variationIndex: number,
    field: CopyFieldKey,
    mode: CopyFieldMode,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          mode,
        },
      },
    }))
  }

  const handleCopyPromptChange = (
    roundNumber: number,
    variationIndex: number,
    field: CopyFieldKey,
    prompt: string,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          prompt,
        },
      },
    }))
  }

  const handleSignatureModeChange = (
    roundNumber: number,
    variationIndex: number,
    field: SignatureFieldKey,
    mode: SignatureFieldMode,
  ) => {
    updateVariation(roundNumber, variationIndex, (variation) => ({
      ...variation,
      fields: {
        ...variation.fields,
        [field]: {
          ...variation.fields[field],
          mode,
        },
      },
    }))
  }

  const handleSaveRound = async (roundNumber: number) => {
    setRoundSaveAttempted((current) => ({ ...current, [roundNumber]: true }))

    const round = roundsForm.find((round) => round.roundNumber === roundNumber)
    if (!round) return

    const validation = validateRound(round)
    if (roundHasErrors(validation)) return

    const sanitizedRounds = roundsForm.map(sanitizeRound)
    await saveSettings(`round-${roundNumber}`, { rounds: sanitizedRounds })
  }

  const handleConfirmResetRound = async () => {
    if (resetRoundNumber === null) return

    const roundNumber = resetRoundNumber
    setSavingSection(`reset-${roundNumber}`)
    try {
      const saved = await resetRoundToDefaults(roundNumber).unwrap()
      setTuningForm(settingsToTuningForm(saved))
      setAiForm(settingsToAiForm(saved))
      setAiModelSelection(getAiModelSelection(saved.ai.model))
      setRoundsForm(settingsToRounds(saved))
      setRoundSaveAttempted((current) => ({
        ...current,
        [roundNumber]: false,
      }))
      dispatch(
        setToast({
          show: true,
          variant: "success",
          message: "Spam test settings saved",
        }),
      )
    } catch (error) {
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: getErrorMessage(error, true),
        }),
      )
    } finally {
      setSavingSection(null)
    }
  }

  const handleExportPrompts = () => {
    if (!roundsForm.length) return
    const stamp = new Date().toISOString().slice(0, 10)
    downloadCsvString(
      `spam-test-prompts-${stamp}.csv`,
      roundsToCsv(roundsForm.map(sanitizeRound)),
    )
  }

  const handleImportClick = () => {
    importInputRef.current?.click()
  }

  const handleImportFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset the input so selecting the same file again re-triggers change.
    event.target.value = ""
    if (!file) return

    try {
      const text = await readFileAsText(file)
      const { rounds, error } = csvToRounds(text)
      if (error || !rounds.length) {
        dispatch(
          setToast({
            show: true,
            variant: "error",
            message: error || "No variations found in CSV.",
          }),
        )
        return
      }

      const sanitizedRounds = rounds.map(sanitizeRound)
      const invalidRound = sanitizedRounds.find((round) =>
        roundHasErrors(validateRound(round)),
      )
      if (invalidRound) {
        dispatch(
          setToast({
            show: true,
            variant: "error",
            message: `Round ${invalidRound.roundNumber} in the CSV is invalid. Check prompts and unique variation names.`,
          }),
        )
        return
      }

      const saved = await saveSettings("import", { rounds: sanitizedRounds })
      if (saved) {
        dispatch(
          setToast({
            show: true,
            variant: "success",
            message: `Imported ${sanitizedRounds.reduce(
              (count, round) => count + round.variations.length,
              0,
            )} variations.`,
          }),
        )
      }
    } catch (error) {
      dispatch(
        setToast({
          show: true,
          variant: "error",
          message: getErrorMessage(error, true),
        }),
      )
    }
  }

  if (isError) {
    return <ErrorMessage />
  }

  return (
    <>
      <div className={cx("", className)} {...rest}>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-2xl font-semibold leading-8">
              Spam Test Settings
            </h2>
            <p className="mt-1 text-sm text-base-content/70">
              Configure placement test tuning, copy generation, and elimination
              variations.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <OutlineButton
              type="button"
              className="btn-sm"
              onClick={handleExportPrompts}
              disabled={isLoading || !roundsForm.length}
              data-testid="export-prompts"
            >
              Export prompts (CSV)
            </OutlineButton>
            <OutlineButton
              type="button"
              className="btn-sm"
              onClick={handleImportClick}
              disabled={formDisabled || savingSection === "import"}
              loading={savingSection === "import"}
              data-testid="import-prompts"
            >
              Import prompts (CSV)
            </OutlineButton>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImportFile}
              data-testid="import-prompts-input"
            />
          </div>
        </div>
        <div
          className={cx("mt-4 flex min-h-[292px] flex-col gap-3", {
            "items-center justify-center": isLoading,
          })}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <SettingsSection title="Placement Test Tuning">
                <p className="text-sm text-base-content/70">
                  Variations are flattened into one pool and tested in random
                  batches. Each variation must hit a perfect inbox score on
                  every seed to win; if a batch produces no winner another batch
                  is drawn until one passes or the pool is exhausted.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <TextField
                    topLeftLabel="Variation pool (rounds)"
                    value={roundsForm.length}
                    readOnly
                    className="input-sm"
                    data-testid="elimination-rounds"
                  />
                  {TUNING_FIELDS.map(({ key, label, min, helpText }) => (
                    <div key={key} className="flex flex-col gap-1">
                      <TextField
                        topLeftLabel={label}
                        type="number"
                        min={min}
                        step={1}
                        value={tuningForm[key]}
                        onChange={handleTuningChange(key)}
                        className="input-sm"
                        disabled={formDisabled}
                        data-testid={`tuning-${key}`}
                      />
                      <span className="text-xs text-base-content/60">
                        {helpText}
                      </span>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  onClick={handleSaveTuning}
                  disabled={formDisabled || !tuningFormValid}
                  loading={savingSection === "tuning"}
                  className="btn-sm min-w-[86px]"
                  data-testid="save-test-tuning"
                >
                  Save
                </Button>
              </SettingsSection>

              <SettingsSection title="AI Copy Generation">
                <p className="text-sm text-base-content/70">
                  Configure the model, reasoning, sampling, and retry behavior
                  used to generate copy variations.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <Select
                    label="Model"
                    placeholder="Use env/default"
                    allowClear
                    value={aiModelSelection}
                    options={AI_MODEL_OPTIONS}
                    onChange={(value) => handleAiModelChange(value as string)}
                    disabled={formDisabled}
                    containerClassName="w-full"
                    className="w-full"
                    data-testid="ai-model-select"
                  />
                  <Select
                    label="Reasoning effort"
                    value={aiForm.reasoningEffort}
                    options={REASONING_EFFORT_OPTIONS}
                    onChange={(value) =>
                      handleReasoningEffortChange(value as string)
                    }
                    disabled={formDisabled}
                    containerClassName="w-full"
                    className="w-full"
                    data-testid="ai-reasoning-effort-select"
                  />
                  <TextField
                    topLeftLabel="Temperature"
                    type="number"
                    min={0}
                    max={2}
                    step={0.1}
                    placeholder="Model default"
                    value={aiForm.temperature ?? ""}
                    onChange={handleAiSamplingChange("temperature")}
                    className="input-sm"
                    disabled={formDisabled}
                    data-testid="ai-temperature"
                  />
                  <TextField
                    topLeftLabel="Top P"
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    placeholder="Model default"
                    value={aiForm.topP ?? ""}
                    onChange={handleAiSamplingChange("topP")}
                    className="input-sm"
                    disabled={formDisabled}
                    data-testid="ai-top-p"
                  />
                  <TextField
                    topLeftLabel="Timeout ms"
                    type="number"
                    min={1}
                    step={1}
                    value={aiForm.timeoutMs}
                    onChange={handleAiNumberChange("timeoutMs")}
                    className="input-sm"
                    disabled={formDisabled}
                    data-testid="ai-timeout-ms"
                  />
                  <TextField
                    topLeftLabel="Max retries"
                    type="number"
                    min={0}
                    step={1}
                    value={aiForm.maxRetries}
                    onChange={handleAiNumberChange("maxRetries")}
                    className="input-sm"
                    disabled={formDisabled}
                    data-testid="ai-max-retries"
                  />
                </div>
                <p className="text-xs text-base-content/60">
                  Reasoning models (gpt-5.x) ignore temperature/top P; leave
                  those blank for them. Older models (gpt-4o) ignore reasoning
                  effort. Blank fields fall back to the model default.
                </p>
                {aiModelSelection === CUSTOM_MODEL_VALUE && (
                  <TextField
                    topLeftLabel="Custom model"
                    value={aiForm.model}
                    placeholder="Leave blank to use env/default"
                    onChange={({ target: { value } }) =>
                      setAiForm((current) => ({ ...current, model: value }))
                    }
                    className="input-sm"
                    disabled={formDisabled}
                    data-testid="ai-custom-model-input"
                  />
                )}
                <Button
                  type="button"
                  onClick={handleSaveAi}
                  disabled={formDisabled || !aiFormValid}
                  loading={savingSection === "ai"}
                  className="btn-sm min-w-[86px]"
                  data-testid="save-ai-generation"
                >
                  Save
                </Button>
              </SettingsSection>

              <div className="space-y-1">
                <h3 className="text-lg font-semibold">Elimination Rounds</h3>
                <p className="text-sm text-base-content/70">
                  Manage the copy and signature variations tested in each
                  elimination round.
                </p>
              </div>

              {roundsForm.map((round) => (
                <RoundSection
                  key={round.roundNumber}
                  round={round}
                  errors={
                    roundSaveAttempted[round.roundNumber]
                      ? roundValidationByNumber[round.roundNumber]
                      : undefined
                  }
                  disabled={formDisabled}
                  savingSection={savingSection}
                  onAddVariation={() => handleAddVariation(round.roundNumber)}
                  onResetRound={() => setResetRoundNumber(round.roundNumber)}
                  onSaveRound={() => handleSaveRound(round.roundNumber)}
                  onRemoveVariation={(variationIndex) =>
                    handleRemoveVariation(round.roundNumber, variationIndex)
                  }
                  onVariationNameChange={(variationIndex, value) =>
                    handleVariationNameChange(
                      round.roundNumber,
                      variationIndex,
                      value,
                    )
                  }
                  onCopyModeChange={(variationIndex, field, mode) =>
                    handleCopyModeChange(
                      round.roundNumber,
                      variationIndex,
                      field,
                      mode,
                    )
                  }
                  onCopyPromptChange={(variationIndex, field, prompt) =>
                    handleCopyPromptChange(
                      round.roundNumber,
                      variationIndex,
                      field,
                      prompt,
                    )
                  }
                  onSignatureModeChange={(variationIndex, field, mode) =>
                    handleSignatureModeChange(
                      round.roundNumber,
                      variationIndex,
                      field,
                      mode,
                    )
                  }
                />
              ))}
            </>
          )}
        </div>
      </div>
      <ConfirmModal
        open={resetRoundNumber !== null}
        onClose={() => setResetRoundNumber(null)}
        onConfirm={handleConfirmResetRound}
        message={
          <span>
            Reset Round {resetRoundNumber} to defaults? Unsaved changes in this
            round will be lost.
          </span>
        }
      />
    </>
  )
}

export default AdminSpamTestSettingsPage
